import { useState } from 'react';
import {
  Mail, Lock, Eye, EyeOff, ArrowRight,
  Globe, ChevronDown, ShieldCheck, UserRound, UserCog,
} from 'lucide-react';
import { apiLogin, apiRegister, setAuthSession } from '../api/client';
import { LOGIN_PRESETS } from '../lib/sampleData';

const PRESET_ICONS = { SuperAdmin: UserCog, Admin: ShieldCheck, Member: UserRound };

export default function LoginPage({ onLogin, language, setLanguage, supportedLocales = [], t }) {
  const [email, setEmail]       = useState('ahmed@coretask.dev');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [error, setError]       = useState('');
  const [pending, setPending]   = useState(null);

  const runLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) { setError(t('loginValidationBoth')); return; }
    setError(''); setPending('login');
    try {
      const d = await apiLogin({ email: email.trim(), password });
      const em = typeof d.email === 'string' ? d.email : email.trim();
      setAuthSession({ token: d.token, email: em });
      onLogin({ email: em });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('loginValidationBoth'));
    } finally { setPending(null); }
  };

  const runRegister = async () => {
    if (!email.trim() || !password.trim()) { setError(t('loginValidationBoth')); return; }
    setError(''); setPending('register');
    try {
      const d = await apiRegister({ email: email.trim(), password });
      const em = typeof d.email === 'string' ? d.email : email.trim();
      setAuthSession({ token: d.token, email: em });
      onLogin({ email: em });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('loginValidationBoth'));
    } finally { setPending(null); }
  };

  return (
    <div className="flex min-h-screen bg-white overflow-hidden">

      {/* ── LEFT — fixed, full screen height, half width ─────────── */}
      <div className="fixed hidden h-screen w-1/2 lg:block">
        <img
          src={`${process.env.PUBLIC_URL || ''}/images/login_back.png`}
          alt=""
          className="h-full w-full object-contain"
          draggable={false}
        />
      </div>
      {/* spacer so right panel shifts over */}
      <div className="hidden lg:block lg:w-1/2" />

      {/* ── RIGHT — form ──────────────────────────────────────────── */}
      <div className="flex w-full flex-col bg-white lg:w-1/2">

        {/* top bar */}
        <div className="flex items-center justify-between px-8 pt-7">

          {/* language */}
          <div className="relative inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 shadow-sm">
            <Globe size={13} className="shrink-0 text-slate-400" />
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="cursor-pointer appearance-none bg-transparent outline-none pe-4"
            >
              {supportedLocales.map((l) => (
                <option key={l.code} value={l.code}>{l.nativeLabel}</option>
              ))}
            </select>
            <ChevronDown size={11} className="pointer-events-none absolute end-2 text-slate-400" />
          </div>
        </div>

        {/* form — vertically centered */}
        <div className="flex flex-1 flex-col justify-center px-16 py-10 sm:px-20 xl:px-24">

          {/* login.jpeg banner */}
          <div className="mb-6 overflow-hidden rounded-2xl">
            <img
              src={`${process.env.PUBLIC_URL || ''}/images/login.jpeg`}
              alt=""
              className="h-32 w-full object-contain"
              draggable={false}
            />
          </div>

          <h1 className="text-3xl font-extrabold text-slate-900">{t('loginTitle')}</h1>
          <p className="mt-2 text-sm text-slate-500">{t('loginSubtitle')}</p>

          <form onSubmit={runLogin} className="mt-8 space-y-5">

            {/* Email */}
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                {t('email')}
              </label>
              <div className="relative w-4/5">
                <Mail size={16} className="pointer-events-none absolute start-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  disabled={pending !== null}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder={t('emailPlaceholder')}
                  className="w-full rounded border border-slate-200 bg-white py-4 ps-11 pe-4 text-sm text-slate-800 placeholder-slate-400 outline-none transition focus:border-brand-400 focus:ring-4 focus:ring-brand-100 disabled:opacity-60"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="mb-1.5 flex w-4/5 items-center justify-between">
                <label className="text-sm font-semibold text-slate-700">{t('password')}</label>
                <button type="button" className="text-xs font-semibold text-brand-600 hover:text-brand-800 transition">
                  {t('forgotPassword')}
                </button>
              </div>
              <div className="relative w-4/5">
                <Lock size={16} className="pointer-events-none absolute start-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  disabled={pending !== null}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder={t('passwordPlaceholder')}
                  className="w-full rounded border border-slate-200 bg-white py-4 ps-11 pe-11 text-sm text-slate-800 placeholder-slate-400 outline-none transition focus:border-brand-400 focus:ring-4 focus:ring-brand-100 disabled:opacity-60"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute end-3.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 transition"
                >
                  {showPw ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <p className="w-4/5 rounded border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600">
                {error}
              </p>
            )}

            {/* Sign In */}
            <button
              type="submit"
              disabled={pending !== null}
              className="group flex w-4/5 items-center justify-center gap-2 rounded bg-brand-600 py-4 text-sm font-bold text-white shadow-lg shadow-brand/25 transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {pending === 'login' ? t('signingIn') : t('signIn')}
              {pending !== 'login' && (
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              )}
            </button>

            {/* Register */}
            <button
              type="button"
              disabled={pending !== null}
              onClick={runRegister}
              className="w-4/5 rounded border border-slate-200 bg-white py-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {pending === 'register' ? t('creatingAccount') : t('createAccount')}
            </button>

            {/* Quick fill */}
            <div className="w-4/5">
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {t('loginPresetHint')}
                </span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {LOGIN_PRESETS.map((preset) => {
                  const Icon = PRESET_ICONS[preset.role] ?? UserRound;
                  return (
                    <button
                      key={preset.role}
                      type="button"
                      disabled={pending !== null}
                      onClick={() => { setEmail(preset.email); setPassword(preset.password); setError(''); }}
                      className="flex items-center justify-center gap-1.5 rounded border border-slate-200 bg-white py-3 text-xs font-semibold text-slate-600 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Icon size={13} className="shrink-0" />
                      {t(preset.labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>

          </form>
        </div>

        {/* footer */}
        <p className="pb-7 text-center text-xs text-slate-400">
          © 2025 CoreTask Workspace. All rights reserved.
        </p>
      </div>
    </div>
  );
}
